import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api-client';
import type {
  DuplicateSummary,
  DuplicatesListResponse,
  DuplicateConfidence,
} from '@/types/api';

interface DuplicateListParams {
  confidence?: DuplicateConfidence;
  limit?: number;
  offset?: number;
}

// Fetch duplicates list
async function fetchDuplicates(params: DuplicateListParams = {}): Promise<DuplicatesListResponse> {
  const searchParams = new URLSearchParams();
  if (params.confidence) searchParams.append('confidence', params.confidence);
  if (params.limit) searchParams.append('limit', params.limit.toString());
  if (params.offset) searchParams.append('offset', params.offset.toString());

  const queryString = searchParams.toString();
  const url = `/api/v1/duplicates${queryString ? `?${queryString}` : ''}`;
  return apiClient.get<DuplicatesListResponse>(url);
}

// Fetch duplicates summary
async function fetchDuplicateSummary(): Promise<DuplicateSummary> {
  const response = await apiClient.get<{ data: DuplicateSummary }>('/api/v1/duplicates/summary');
  return response.data;
}

// Dismiss a duplicate
// The server attributes the dismissal to the authenticated administrator (FR-178).
async function dismissDuplicate(duplicateId: string): Promise<{ success: boolean }> {
  return apiClient.post<{ success: boolean }>(`/api/v1/duplicates/${duplicateId}/dismiss`, {});
}

// Query keys
export const duplicateKeys = {
  all: ['duplicates'] as const,
  list: (params: DuplicateListParams) => [...duplicateKeys.all, 'list', params] as const,
  summary: () => [...duplicateKeys.all, 'summary'] as const,
};

// Hooks
export function useDuplicates(params: DuplicateListParams = {}) {
  return useQuery({
    queryKey: duplicateKeys.list(params),
    queryFn: () => fetchDuplicates(params),
  });
}

export function useDuplicateSummary() {
  return useQuery({
    queryKey: duplicateKeys.summary(),
    queryFn: fetchDuplicateSummary,
  });
}

export function useDismissDuplicate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ duplicateId }: { duplicateId: string }) => dismissDuplicate(duplicateId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: duplicateKeys.all });
    },
  });
}
